import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { api } from "@/api/client";
import type {
  PiiDetection,
  PiiFilterConfig,
  PiiFilterConfigPatch,
} from "@/api/types";

const KEY_CONFIG = ["pii", "config"] as const;
const KEY_DETECTIONS = ["pii", "detections"] as const;
const KEY_PROMPT_HISTORY = ["pii", "prompt-history"] as const;

export interface PiiPromptVersion {
  version: number | null;
  content: string;
  labels: string[];
  commit_message: string | null;
  created_at: string | null;
}

export function usePiiConfig() {
  return useQuery({
    queryKey: KEY_CONFIG,
    queryFn: () => api.GET<PiiFilterConfig>("/pii/config"),
  });
}

export function useUpdatePiiConfig() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (patch: PiiFilterConfigPatch) =>
      api.PATCH<PiiFilterConfig>("/pii/config", patch),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: KEY_CONFIG });
    },
  });
}

export interface PiiDetectionFilters {
  thread_id?: string;
  category?: string;
  direction?: "input" | "output";
  since?: string;
  until?: string;
  limit?: number;
  offset?: number;
}

export function usePiiPromptHistory(enabled: boolean) {
  return useQuery({
    queryKey: KEY_PROMPT_HISTORY,
    queryFn: () => api.GET<PiiPromptVersion[]>("/pii/prompt-history"),
    enabled,
  });
}

export function useRollbackPiiPrompt() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload: { version: number; commit_message?: string }) =>
      api.POST<PiiFilterConfig>("/pii/prompt/rollback", payload),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: KEY_CONFIG });
      void qc.invalidateQueries({ queryKey: KEY_PROMPT_HISTORY });
    },
  });
}

export function usePiiDetections(filters: PiiDetectionFilters = {}) {
  return useQuery({
    queryKey: [...KEY_DETECTIONS, filters],
    queryFn: () => {
      const params: Record<string, string | number> = {};
      if (filters.thread_id) params.thread_id = filters.thread_id;
      if (filters.category) params.category = filters.category;
      if (filters.direction) params.direction = filters.direction;
      if (filters.since) params.since = filters.since;
      if (filters.until) params.until = filters.until;
      if (filters.limit) params.limit = filters.limit;
      if (filters.offset) params.offset = filters.offset;
      return api.GET<PiiDetection[]>("/pii/detections", { params });
    },
  });
}
